import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, TriangleAlert, Users, X } from 'lucide-react';
import { CampoFecha } from '@components/CampoFecha';
import {
  getContinuidad,
  type ContinuidadIntegrante,
  type PreviewBajaBinItem,
} from '@lib/services/bajas.service';
import {
  MOTIVOS,
  TB_EMPRESA,
  TB_FALLECIMIENTO,
  VIGENCIA_EMPRESA,
  motivoNoConfirmable,
  toDDMMYYYY,
  toISO,
  vigenciaPorFallecimiento,
  type FilaRev,
  type PlanRearmado,
} from '@lib/bajas-revision';
import {
  REPARTO_VACIO,
  abrirRama,
  asignar,
  construirGrupos,
  darDeBaja,
  decisionesTomadas,
  motivoNoPuede,
  puedeQuedarACargo,
  puedeQuedarCon,
  ramas,
  repartoCompleto,
  resumenReparto,
  siguientePendiente,
  totalDecisiones,
  type EstadoReparto,
} from '@lib/rearmado';

/**
 * Rearmado de grupo: se da de baja al titular y se decide quién queda a cargo
 * de los que siguen.
 *
 * Se pregunta de a UNA persona. Un grupo puede terminar partido en varios —dos
 * hijos grandes que arman cada uno el suyo—, y una pantalla con todo el árbol a
 * la vista hacía que se pasara por alto a alguien; el que no queda asignado se
 * queda SIN COBERTURA, porque Binary reemplaza la foto entera del grupo.
 *
 * **Acá no se escribe nada.** El reparto se guarda en la fila y se ejecuta con
 * el resto del lote, al confirmar. Antes se despachaba al cerrar el modal: el
 * operador todavía estaba revisando el archivo y la baja ya estaba hecha.
 */
export function RearmarGrupoModal({
  item,
  plan,
  onClose,
  onPlan,
}: {
  item: PreviewBajaBinItem;
  /** El reparto que ya se había armado para esta fila, si se vuelve a abrir. */
  plan?: PlanRearmado;
  onClose: () => void;
  onPlan: (plan: PlanRearmado) => void;
}) {
  const [estado, setEstado] = useState<EstadoReparto>(REPARTO_VACIO);
  /** Cada respuesta apila el estado anterior: "Volver" deshace de a una. */
  const [historial, setHistorial] = useState<EstadoReparto[]>([]);
  const [tb, setTb] = useState(plan?.tb ?? item.tbCodigo ?? '');
  const [vig, setVig] = useState(plan?.vig ?? toISO(item.fechaVigencia));
  const [fall, setFall] = useState(plan?.fall ?? toISO(item.fechaFallecimiento));

  const q = useQuery({
    queryKey: ['continuidad', item.dni],
    queryFn: () => getContinuidad(item.dni),
    retry: false,
  });

  const integrantes = useMemo(() => q.data?.integrantes ?? [], [q.data]);
  const lista = useMemo(() => ramas(integrantes, estado), [integrantes, estado]);
  const pendiente = siguientePendiente(integrantes, estado);
  const completo = repartoCompleto(integrantes, estado);

  function responder(siguiente: EstadoReparto) {
    setHistorial((h) => [...h, estado]);
    setEstado(siguiente);
  }
  function volver() {
    setHistorial((h) => {
      if (h.length === 0) return h;
      setEstado(h[h.length - 1]);
      return h.slice(0, -1);
    });
  }

  const faltaDato = motivoNoConfirmable({ item, tb, vig, fall, sel: false } as FilaRev);

  function guardar() {
    onPlan({
      grupos: construirGrupos(estado),
      dadosDeBaja: estado.bajas,
      tb,
      vig,
      fall,
      resumen: resumenReparto(lista, item.nombre || item.dni, estado.bajas.length),
    });
  }

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div className="modalCard" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <header className="modalHeader">
          <div>
            <h2 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={20} /> Rearmado de grupo
            </h2>
            <p className="text-element-mid" style={{ marginTop: 4, fontSize: 13 }}>
              Se da de baja a <strong>{item.nombre || item.dni}</strong>
              {item.legajo && ` · legajo ${item.legajo}`}. Hay que decidir qué pasa con cada uno de
              los que siguen.
            </p>
          </div>
          <button type="button" className="modalClose" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </header>

        {q.isLoading && (
          <p className="text-element-mid" style={{ fontSize: 14 }}>
            Leyendo el grupo en Binary…
          </p>
        )}

        {q.isError && (
          <Aviso tono="error">
            No se pudo leer el grupo: {mensajeError(q.error)}. Sin el grupo en vivo no se puede
            rearmar nada.
          </Aviso>
        )}

        {q.data?.caeElGrupo && (
          <>
            <Aviso tono="error">
              Nadie del grupo puede quedar a cargo, así que esta baja se lleva el legajo completo y
              no se resuelve acá. Queda registrada en <strong>Alertas Bajas</strong> para que la
              gestione Medicus.
            </Aviso>
            <ListaIntegrantes integrantes={integrantes} />
          </>
        )}

        {q.data && !q.data.caeElGrupo && (
          <>
            <Progreso hechas={decisionesTomadas(estado)} total={totalDecisiones(integrantes)} />

            {!completo && (
              <div
                className="rgCard"
                key={estado.titulares.length === 0 ? 'cabeza' : pendiente?.afiId}
              >
                {estado.titulares.length === 0 ? (
                  <Pregunta
                    titulo="¿Quién queda a cargo del legajo?"
                    ayuda="Es quien pasa a ser titular y se queda con el legajo actual. Después se decide, de a uno, qué pasa con el resto."
                  >
                    {integrantes.map((i) => (
                      <OpcionPersona
                        key={i.afiId}
                        integrante={i}
                        onClick={() => responder(abrirRama(estado, i.afiId))}
                      />
                    ))}
                  </Pregunta>
                ) : (
                  pendiente && (
                    <Pregunta
                      titulo={`¿Qué pasa con ${etiqueta(pendiente)}?`}
                      ayuda={detalleDe(pendiente)}
                    >
                      {lista.map((r) => {
                        const puede = puedeQuedarCon(pendiente, r.titular.afiId);
                        if (!puede) {
                          return (
                            <div key={r.titular.afiId} className="rgOpcion rgOpcion--off">
                              <span style={{ fontWeight: 600 }}>
                                No puede quedar con {etiqueta(r.titular)}
                              </span>
                              <span className="text-element-mid" style={{ fontSize: 12 }}>
                                No hay forma de deducir qué relación tienen, y un parentesco
                                inventado en Binary no lo revisa nadie después.
                              </span>
                            </div>
                          );
                        }
                        return (
                          <button
                            key={r.titular.afiId}
                            type="button"
                            className="rgOpcion"
                            onClick={() =>
                              responder(asignar(estado, pendiente.afiId, r.titular.afiId))
                            }
                          >
                            <span style={{ fontWeight: 600 }}>Queda con {etiqueta(r.titular)}</span>
                            <span className="text-element-mid" style={{ fontSize: 12 }}>
                              {r.esPrincipal
                                ? `Sigue en el legajo ${item.legajo || 'actual'}`
                                : 'En el legajo nuevo que abre'}
                              {r.miembros.length > 0 && ` · ya tiene ${r.miembros.length}`}
                            </span>
                          </button>
                        );
                      })}

                      {puedeQuedarACargo(pendiente) ? (
                        <button
                          type="button"
                          className="rgOpcion rgOpcion--nueva"
                          onClick={() => responder(abrirRama(estado, pendiente.afiId))}
                        >
                          <span style={{ fontWeight: 600 }}>Arma su propio grupo</span>
                          <span className="text-element-mid" style={{ fontSize: 12 }}>
                            Pasa a ser titular y se le abre un legajo nuevo, derivado de{' '}
                            {item.legajo || 'este'}
                          </span>
                        </button>
                      ) : (
                        // Se muestra igual, deshabilitada y con el motivo: si la
                        // opción no apareciera, el operador no sabría por qué a
                        // esta persona no se la puede dejar a cargo.
                        <div className="rgOpcion rgOpcion--off">
                          <span style={{ fontWeight: 600 }}>No puede armar su propio grupo</span>
                          <span className="text-element-mid" style={{ fontSize: 12 }}>
                            {motivoNoPuede(pendiente)}
                          </span>
                        </div>
                      )}

                      {/* La tercera salida, y la única para el adherente o el
                          nieto que nadie se lleva. */}
                      <button
                        type="button"
                        className="rgOpcion rgOpcion--baja"
                        onClick={() => responder(darDeBaja(estado, pendiente.afiId))}
                      >
                        <span style={{ fontWeight: 600 }}>Se da de baja</span>
                        <span className="text-element-mid" style={{ fontSize: 12 }}>
                          No sigue en ningún grupo: se da de baja junto con{' '}
                          {item.nombre || 'el titular'}, con el mismo motivo y la misma vigencia
                        </span>
                      </button>
                    </Pregunta>
                  )
                )}
              </div>
            )}

            {(lista.length > 0 || estado.bajas.length > 0) && (
              <ComoVaQuedando
                ramas={lista}
                bajas={integrantes.filter((i) => estado.bajas.includes(i.afiId))}
                legajo={item.legajo}
              />
            )}

            {completo && (
              <div className="rgCard" key="fechas">
                <h3 className="heading-4" style={{ marginBottom: 4 }}>
                  Con qué motivo y desde cuándo
                </h3>
                <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 12 }}>
                  {resumenReparto(lista, item.nombre || item.dni, estado.bajas.length)}
                </p>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label style={{ fontSize: 13 }}>
                    <div style={{ marginBottom: 4 }}>Motivo</div>
                    <select
                      value={tb}
                      onChange={(e) => setTb(e.target.value)}
                      style={{ ...campoStyle, minWidth: 230 }}
                    >
                      <option value="">— elegí un motivo —</option>
                      {MOTIVOS.map((m) => (
                        <option key={m.codigo} value={m.codigo}>
                          {m.label} ({m.codigo})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ fontSize: 13 }}>
                    <div style={{ marginBottom: 4 }}>Vigencia</div>
                    {tb === TB_EMPRESA ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{VIGENCIA_EMPRESA}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>
                          Baja por empresa: siempre el 1° del mes próximo
                        </div>
                      </>
                    ) : tb === TB_FALLECIMIENTO ? (
                      // Igual que en la revisión: la vigencia de un fallecimiento
                      // se deriva del deceso y lo que se tipeara acá se descartaba
                      // al confirmar (hallazgo del review).
                      <>
                        <div style={{ fontWeight: 600 }}>
                          {fall ? toDDMMYYYY(vigenciaPorFallecimiento(fall)) : '—'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>
                          Fallecimiento: el 1° del mes siguiente al deceso
                        </div>
                      </>
                    ) : (
                      <CampoFecha value={vig} onChange={setVig} />
                    )}
                  </label>

                  {tb === TB_FALLECIMIENTO && (
                    <label style={{ fontSize: 13 }}>
                      <div style={{ marginBottom: 4 }}>Fecha de fallecimiento</div>
                      <CampoFecha value={fall} onChange={setFall} />
                    </label>
                  )}
                </div>

                <Aviso tono="alerta">
                  Esto no se manda todavía: queda guardado en la fila y se ejecuta al{' '}
                  <strong>confirmar el lote</strong>, junto con el resto. Ahí sí se arma el grupo
                  nuevo en Binary y después se da de baja, en ese orden, y no se deshace solo.
                </Aviso>
              </div>
            )}
          </>
        )}

        <footer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 18,
            flexWrap: 'wrap',
          }}
        >
          <div>
            {historial.length > 0 && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={volver}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <ArrowLeft size={15} /> Volver
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            {completo && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!!faltaDato}
                title={faltaDato || undefined}
                onClick={guardar}
              >
                Guardar en el lote
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---- Piezas ----

const campoStyle: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--color-outline)',
  borderRadius: 8,
  fontSize: 12.5,
};

/** "Cónyuge, 45 años" — de cara al usuario nunca va el código de parentesco. */
function etiqueta(i: ContinuidadIntegrante): string {
  const edad = i.edad === null ? '' : `, ${i.edad} años`;
  return `${i.parentescoLabel.toLowerCase()}${edad}`;
}

function detalleDe(i: ContinuidadIntegrante): string {
  return i.cuil ? `CUIL ${i.cuil} · afi ${i.afiId}` : `afi ${i.afiId}`;
}

function Pregunta({
  titulo,
  ayuda,
  children,
}: {
  titulo: string;
  ayuda?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="heading-4" style={{ marginBottom: 2 }}>
        {titulo}
      </h3>
      {ayuda && (
        <p className="text-element-mid" style={{ fontSize: 12.5, marginBottom: 10 }}>
          {ayuda}
        </p>
      )}
      <div style={{ display: 'grid', gap: 8 }}>{children}</div>
    </div>
  );
}

/**
 * Una persona como opción. Los que no pueden quedar a cargo SE MUESTRAN igual,
 * apagados y con el motivo: sacarlos de la lista hace creer que falta gente del
 * grupo.
 */
function OpcionPersona({
  integrante,
  onClick,
}: {
  integrante: ContinuidadIntegrante;
  onClick: () => void;
}) {
  if (!puedeQuedarACargo(integrante)) {
    return (
      <div className="rgOpcion rgOpcion--off">
        <span style={{ fontWeight: 600 }}>{cap(etiqueta(integrante))}</span>
        <span className="text-element-mid" style={{ fontSize: 12 }}>
          {motivoNoPuede(integrante)}
        </span>
      </div>
    );
  }
  return (
    <button type="button" className="rgOpcion" onClick={onClick}>
      <span style={{ fontWeight: 600 }}>{cap(etiqueta(integrante))}</span>
      <span className="text-element-mid" style={{ fontSize: 12 }}>
        {detalleDe(integrante)}
      </span>
    </button>
  );
}

function ListaIntegrantes({ integrantes }: { integrantes: ContinuidadIntegrante[] }) {
  if (integrantes.length === 0) return null;
  return (
    <ul style={{ marginTop: 12, marginLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
      {integrantes.map((i) => (
        <li key={i.afiId}>
          <strong>{cap(etiqueta(i))}</strong> — {i.detalle}
        </li>
      ))}
    </ul>
  );
}

function Progreso({ hechas, total }: { hechas: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((hechas / total) * 100);
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 12,
          color: 'var(--color-element-mid)',
          marginBottom: 4,
        }}
      >
        <span>
          {hechas} de {total} decidido(s)
        </span>
        <span>{pct}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 99, background: 'var(--color-overlay, #eef1f5)' }}>
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 99,
            background: 'var(--color-primary)',
            transition: 'width .25s ease',
          }}
        />
      </div>
    </div>
  );
}

function ComoVaQuedando({
  ramas: lista,
  bajas,
  legajo,
}: {
  ramas: ReturnType<typeof ramas>;
  bajas: ContinuidadIntegrante[];
  legajo?: string;
}) {
  return (
    <div
      style={{
        marginTop: 16,
        padding: 12,
        borderRadius: 10,
        background: 'var(--color-surface-accent, #f8f9fb)',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Cómo va quedando</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {lista.map((r) => (
          <div key={r.titular.afiId}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {cap(etiqueta(r.titular))}
              <span
                className="text-element-mid"
                style={{ fontWeight: 400, fontSize: 12, marginLeft: 6 }}
              >
                {r.esPrincipal ? `legajo ${legajo || 'actual'}` : 'legajo nuevo'}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--color-element-mid)', marginLeft: 12 }}>
              {r.miembros.length === 0
                ? 'sin nadie más'
                : r.miembros.map((m) => etiqueta(m)).join(' · ')}
            </div>
          </div>
        ))}
        {bajas.length > 0 && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>Se dan de baja</div>
            <div style={{ fontSize: 12.5, color: 'var(--color-element-mid)', marginLeft: 12 }}>
              {bajas.map((b) => etiqueta(b)).join(' · ')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TONOS = {
  alerta: { color: '#b45309', bg: '#f8ecd2' },
  error: { color: '#b91c1c', bg: '#f9e2e5' },
};

function Aviso({ tono, children }: { tono: keyof typeof TONOS; children: React.ReactNode }) {
  const t = TONOS[tono];
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        background: t.bg,
        color: t.color,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <TriangleAlert size={16} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>{children}</div>
    </div>
  );
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

function mensajeError(e: unknown): string {
  const r = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message;
  if (Array.isArray(r)) return r.join(' · ');
  if (typeof r === 'string') return r;
  return e instanceof Error ? e.message : 'Error inesperado';
}
